import type { LucideIcon } from "lucide-react";
import {
  CalendarCheck,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  CornerUpRight,
  Mic,
  PenLine,
  PhoneCall,
  Search,
} from "lucide-react";

export type MarketingActionStep = {
  icon: LucideIcon;
  title: string;
  body: string;
};

export type MarketingHeroAction = {
  icon: LucideIcon;
  label: string;
  tone?: "primary" | "neutral";
};

export type MarketingHeroWidget = {
  icon: LucideIcon;
  label: string;
  value: string;
  note: string;
};

export type MarketingScenario = {
  id: "book" | "call" | "followup";
  label: string;
  message: string;
  messageType?: "voice" | "text";
  voiceDuration?: string;
  steps: MarketingActionStep[];
  result: string;
  hero: {
    youNote: string;
    assistantNote: string;
    actions: MarketingHeroAction[];
    widgets: MarketingHeroWidget[];
  };
};

export const MARKETING_SCENARIOS: MarketingScenario[] = [
  {
    id: "book",
    label: "Book",
    message: "Book Sushi Palace for two, Friday at 7.",
    messageType: "text",
    steps: [
      { icon: Search, title: "Find options", body: "Checks reviews, hours, and availability." },
      { icon: PhoneCall, title: "Make the call", body: "Reserves with the details you gave." },
      {
        icon: CheckCircle2,
        title: "Confirm back to you",
        body: "Sends the confirmation, address, and notes.",
      },
      {
        icon: CalendarCheck,
        title: "Follow up automatically",
        body: "Reminds you at the right time so nothing slips.",
      },
    ],
    result: "Reservation confirmed. Dobbix will remind you before you leave.",
    hero: {
      youNote: "No setup. Just send a message like normal.",
      assistantNote: "One message in. Actions out. Quiet follow-through.",
      actions: [
        { icon: CheckCircle2, label: "Booked Sushi Palace · Fri 7:00 PM", tone: "primary" },
        { icon: PhoneCall, label: "Called to confirm seating preferences" },
        { icon: CalendarCheck, label: "Reminder scheduled · 6:10 PM" },
      ],
      widgets: [
        { icon: PhoneCall, label: "Calls", value: "1", note: "Handled today" },
        { icon: CalendarCheck, label: "Reminders", value: "3", note: "Queued gently" },
        { icon: CornerUpRight, label: "Follow-ups", value: "2", note: "Drafted + ready" },
      ],
    },
  },
  {
    id: "call",
    label: "Call",
    message: "Reschedule my dentist to next week.",
    messageType: "voice",
    voiceDuration: "0:12",
    steps: [
      { icon: Mic, title: "Understand voice note", body: "Transcribes and extracts the request." },
      { icon: PhoneCall, title: "Call the office", body: "Finds a time that matches your ask." },
      { icon: ClipboardCheck, title: "Update the plan", body: "Sends a summary of the new time." },
      { icon: CalendarCheck, title: "Remind + prep", body: "Adds a nudge and any prep notes." },
    ],
    result: "Appointment moved. Dobbix owns the reminders.",
    hero: {
      youNote: "Talk normally. Dobbix handles the call.",
      assistantNote: "Calls made. Calendar updated. You just get the result.",
      actions: [
        { icon: PhoneCall, label: "Called the dentist office" },
        { icon: CheckCircle2, label: "Appointment moved · Wed 2:30 PM", tone: "primary" },
        { icon: CalendarCheck, label: "Reminder set · Wed 1:40 PM" },
      ],
      widgets: [
        { icon: PhoneCall, label: "Calls", value: "2", note: "Placed for you" },
        { icon: CalendarCheck, label: "Reminders", value: "2", note: "Already set" },
        { icon: CornerUpRight, label: "Follow-ups", value: "1", note: "Handled quietly" },
      ],
    },
  },
  {
    id: "followup",
    label: "Follow-up",
    message: "Met Sarah today. Follow up Tuesday about the proposal.",
    messageType: "text",
    steps: [
      { icon: ClipboardList, title: "Capture it", body: "Stores the task and context." },
      {
        icon: CalendarCheck,
        title: "Schedule the follow-up",
        body: "Sets the reminder for Tuesday.",
      },
      { icon: PenLine, title: "Draft the message", body: "Prepares an email/text in your tone." },
      {
        icon: CheckCircle2,
        title: "Nudge you at go-time",
        body: "Hands you the draft when it matters.",
      },
    ],
    result: "Reminder set. Draft delivered on Tuesday.",
    hero: {
      youNote: "Say it once. Dobbix remembers and follows through.",
      assistantNote: "Reminded at the right time, with a draft ready to send.",
      actions: [
        { icon: CalendarCheck, label: "Follow-up scheduled · Tue 9:00 AM", tone: "primary" },
        { icon: PenLine, label: "Draft prepared · ready to review" },
        { icon: CheckCircle2, label: "Nudge set · with the message attached" },
      ],
      widgets: [
        { icon: CalendarCheck, label: "Reminders", value: "4", note: "Placed for you" },
        { icon: PenLine, label: "Drafts", value: "1", note: "Ready Tuesday" },
        { icon: PhoneCall, label: "Calls", value: "0", note: "Not needed" },
      ],
    },
  },
];
